import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, ShoppingCart, Check, Play, Pause, Download, Volume2, Gift, Target, Sparkles, Eye, Users, Share2, Link as LinkIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';
import SEO from '../components/SEO';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Social media icons
const XIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
);

const FacebookIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
);

const LinkedInIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
);

// Share button component with tracking
const ShareButton = ({ title, url, itemType = 'recording' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    // Use /api/og/ URLs for social media sharing (proper og: tags)
    // Regular page URL for copy to clipboard
    const pageUrl = `https://hypnosismasteryseries.com${url}`;
    const ogUrl = `https://hypnosismasteryseries.com/api/og${url}`;
    const encodedOgUrl = encodeURIComponent(ogUrl);
    const encodedTitle = encodeURIComponent(title);
    
    // Extract slug from URL (e.g., /recording/freedom-from-anxiety -> freedom-from-anxiety)
    const slug = url.split('/').pop();

    // Track share event
    const trackShare = async (platform) => {
        try {
            await axios.post(`${API}/track-share`, {
                item_slug: slug,
                item_type: itemType,
                platform: platform
            });
        } catch (err) {
            // Don't block sharing if tracking fails
            console.log('Share tracking failed:', err);
        }
    };

    const shareLinks = [
        { name: 'X', Icon: XIcon, href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedOgUrl}`, platform: 'twitter' },
        { name: 'Facebook', Icon: FacebookIcon, href: `https://www.facebook.com/sharer/sharer.php?u=${encodedOgUrl}`, platform: 'facebook' },
        { name: 'LinkedIn', Icon: LinkedInIcon, href: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedOgUrl}&title=${encodedTitle}`, platform: 'linkedin' }
    ];

    const handleShareClick = (platform, href) => {
        trackShare(platform);
        setIsOpen(false);
        window.open(href, '_blank', 'noopener,noreferrer');
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(pageUrl);
            setCopied(true);
            trackShare('copy');
            toast.success('Link copied to clipboard');
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            toast.error('Failed to copy link');
        }
    };

    const handleNativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({ title: title, url: ogUrl });
                trackShare('native');
            } catch (err) {
                // User cancelled or error
            }
        } else {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div className="relative">
            <button
                onClick={handleNativeShare}
                className="flex items-center gap-2 text-nature-800/60 hover:text-copper-600 transition-colors"
                data-testid="share-button"
            >
                <Share2 className="w-5 h-5" />
                <span className="font-body">Share</span>
            </button>

            {isOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
                    <div className="absolute left-0 top-full mt-2 bg-white rounded-xl shadow-lg border border-nature-100 p-2 z-50 min-w-[180px]">
                        {shareLinks.map((link) => (
                            <button
                                key={link.name}
                                onClick={() => handleShareClick(link.platform, link.href)}
                                className="flex items-center gap-3 px-3 py-2 text-nature-800 hover:bg-nature-50 rounded-lg transition-colors font-body text-sm w-full text-left"
                            >
                                <link.Icon />
                                {link.name}
                            </button>
                        ))}
                        <button
                            onClick={copyToClipboard}
                            className="flex items-center gap-3 px-3 py-2 text-nature-800 hover:bg-nature-50 rounded-lg transition-colors font-body text-sm w-full"
                        >
                            {copied ? <Check className="w-5 h-5 text-green-500" /> : <LinkIcon className="w-5 h-5" />}
                            {copied ? 'Copied!' : 'Copy link'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export const RecordingDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [recording, setRecording] = useState(null);
    const [loading, setLoading] = useState(true);
    const [relatedRecordings, setRelatedRecordings] = useState([]);
    const [isPlaying, setIsPlaying] = useState(false);
    const [audioProgress, setAudioProgress] = useState(0);
    const [audioDuration, setAudioDuration] = useState(0);
    const audioRef = useRef(null);
    const { addToCart, isInCart } = useCart();
    
    const isFreeRecording = slug === 'freedom-from-insomnia';

    useEffect(() => {
        const fetchRecording = async () => {
            try {
                const response = await axios.get(`${API}/recordings/${slug}`);
                const data = response.data;
                
                // 301 redirect: If URL uses rec-### ID, redirect to canonical slug URL
                if (slug.startsWith('rec-') && data.slug && data.slug !== slug) {
                    // Use replace to simulate 301 redirect (replaces history entry)
                    navigate(`/recording/${data.slug}`, { replace: true });
                    return;
                }
                
                setRecording(data);
                
                // Fetch related recordings from same category
                const allRecordings = await axios.get(`${API}/recordings?category=${encodeURIComponent(data.category)}`);
                setRelatedRecordings(allRecordings.data.filter(r => r.slug !== slug).slice(0, 3));
            } catch (error) {
                console.error('Failed to fetch recording:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchRecording();
    }, [slug, navigate]);
    
    const inCart = recording ? isInCart(recording.id) : false;

    useEffect(() => {
        // Reset audio when recording changes
        setIsPlaying(false);
        setAudioProgress(0);
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
    }, [slug]);

    const handleAddToCart = () => {
        if (!inCart && recording) {
            addToCart(recording);
        }
    };

    const [downloading, setDownloading] = useState(false);
    const handleFreeDownload = async () => {
        if (downloading) return;
        setDownloading(true);
        try {
            const res = await axios.post(`${API}/lead-magnet/signup`, {});
            const link = document.createElement('a');
            link.href = res.data.download_url;
            link.download = 'Freedom from Insomnia.mp3';
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Your free recording is downloading. Enjoy!');
        } catch (e) {
            toast.error('Download failed. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    const togglePlay = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
            setAudioProgress(progress || 0);
        }
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setAudioDuration(audioRef.current.duration);
        }
    };

    const handleAudioEnded = () => {
        setIsPlaying(false);
        setAudioProgress(0);
    };

    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-nature-50 pt-24">
                <div className="max-w-7xl mx-auto px-6 py-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        <div className="aspect-square bg-nature-100 rounded-2xl skeleton" />
                        <div className="space-y-4">
                            <div className="h-8 bg-nature-100 rounded skeleton w-1/3" />
                            <div className="h-12 bg-nature-100 rounded skeleton w-3/4" />
                            <div className="h-24 bg-nature-100 rounded skeleton" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!recording) {
        return (
            <div className="min-h-screen bg-nature-50 pt-24 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="font-heading text-2xl text-nature-900 mb-4">Recording not found</h2>
                    <Link to="/recordings">
                        <Button className="rounded-full">Back to Recordings</Button>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-nature-50 pt-24">
            <SEO 
                title={recording.seo_title || `${recording.title} | Hypnosis Mastery Series`}
                description={recording.seo_description || recording.description}
                canonical={`https://hypnosismasteryseries.com/recording/${recording.slug}`}
                image={recording.cover_image}
            />
            
            {/* Product Schema.org Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Product",
                    "name": recording.seo_title || recording.title,
                    "description": recording.seo_description || recording.description,
                    "image": recording.cover_image,
                    "brand": {
                        "@type": "Brand",
                        "name": "Hypnosis Mastery Series"
                    },
                    "offers": {
                        "@type": "Offer",
                        "price": recording.price,
                        "priceCurrency": "EUR",
                        "availability": "https://schema.org/InStock",
                        "url": `https://hypnosismasteryseries.com/recording/${recording.slug}`
                    },
                    "category": recording.volume,
                    "creator": {
                        "@type": "Person",
                        "name": "Eric Paris"
                    }
                })}
            </script>
            
            <div className="max-w-7xl mx-auto px-6 pt-3 pb-12 lg:pt-12">
                {/* Back Link */}
                <Link 
                    to="/recordings" 
                    className="inline-flex items-center gap-2 text-nature-800/70 hover:text-copper-500 font-body mb-4 lg:mb-8 transition-colors"
                    data-testid="back-to-recordings"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Recordings
                </Link>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                    {/* Mobile header: title, short description, main CTA above the cover */}
                    <div className="lg:hidden opacity-0 animate-fade-in-up" data-testid="mobile-header">
                        <h1 className="font-heading text-3xl font-light text-nature-900 mb-3" data-testid="recording-title-mobile">
                            {recording.title}
                        </h1>
                        <p className="text-nature-800/80 font-body leading-relaxed mb-5" data-testid="mobile-short-description">
                            {recording.description}
                        </p>
                        {isFreeRecording ? (
                            <Button
                                onClick={handleFreeDownload}
                                disabled={downloading}
                                size="lg"
                                className="w-full rounded-full bg-copper-500 hover:bg-copper-600"
                                data-testid="mobile-get-free-recording"
                            >
                                <Download className="w-5 h-5 mr-2" />
                                {downloading ? 'Preparing download…' : 'Get Free Recording'}
                            </Button>
                        ) : (
                            <Button
                                onClick={handleAddToCart}
                                size="lg"
                                className={`w-full rounded-full transition-all ${
                                    inCart 
                                        ? 'bg-nature-800 hover:bg-nature-800' 
                                        : 'bg-copper-500 hover:bg-copper-600'
                                }`}
                                data-testid="mobile-add-to-cart"
                            >
                                {inCart ? (
                                    <>
                                        <Check className="w-5 h-5 mr-2" />
                                        Added to Cart
                                    </>
                                ) : (
                                    <>
                                        <ShoppingCart className="w-5 h-5 mr-2" />
                                        Add to Cart — €{recording.price.toFixed(2)}
                                    </>
                                )}
                            </Button>
                        )}
                    </div>

                    {/* Image */}
                    <div className="opacity-0 animate-fade-in-up">
                        <div className="aspect-square rounded-2xl overflow-hidden shadow-soft">
                            <img 
                                src={recording.cover_image} 
                                alt={recording.title}
                                className="w-full h-full object-cover"
                                referrerPolicy="no-referrer"
                            />
                        </div>
                        
                        {/* Audio Preview Player */}
                        {recording.preview_url && (
                            <div className="mt-6 bg-white rounded-2xl p-5 shadow-soft">
                                <div className="flex items-center gap-2 mb-3">
                                    <Volume2 className="w-5 h-5 text-copper-500" />
                                    <span className="font-heading font-semibold text-nature-900">
                                        Listen to Preview
                                    </span>
                                </div>
                                
                                <audio 
                                    ref={audioRef}
                                    src={recording.preview_url}
                                    onTimeUpdate={handleTimeUpdate}
                                    onLoadedMetadata={handleLoadedMetadata}
                                    onEnded={handleAudioEnded}
                                    preload="metadata"
                                />
                                
                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={togglePlay}
                                        className="w-12 h-12 rounded-full bg-copper-500 hover:bg-copper-600 text-white flex items-center justify-center transition-colors"
                                        data-testid="preview-play-btn"
                                    >
                                        {isPlaying ? (
                                            <Pause className="w-5 h-5" />
                                        ) : (
                                            <Play className="w-5 h-5 ml-1" />
                                        )}
                                    </button>
                                    
                                    <div className="flex-1">
                                        <div className="h-2 bg-nature-100 rounded-full overflow-hidden">
                                            <div 
                                                className="h-full bg-copper-500 transition-all duration-200"
                                                style={{ width: `${audioProgress}%` }}
                                            />
                                        </div>
                                        <div className="flex justify-between mt-1 text-xs text-nature-800/60 font-body">
                                            <span>{formatTime(audioRef.current?.currentTime || 0)}</span>
                                            <span>{formatTime(audioDuration)}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <p className="text-xs text-nature-800/50 font-body mt-3 text-center">
                                    2-minute preview • Full session: {recording.duration}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Details */}
                    <div className="opacity-0 animate-fade-in-up stagger-1">
                        <span className="inline-block px-4 py-2 bg-copper-500/10 text-copper-600 rounded-full text-sm font-body font-medium mb-4">
                            {recording.category}
                        </span>
                        
                        <h1 className="hidden lg:block font-heading text-3xl sm:text-4xl font-light text-nature-900 mb-4" data-testid="recording-title">
                            {recording.title}
                        </h1>
                        
                        <div className="flex flex-wrap items-center gap-4 mb-6">
                            <div className="flex items-center gap-2 text-nature-800/60">
                                <Clock className="w-5 h-5" />
                                <span className="font-body">{recording.duration}</span>
                            </div>
                            <div className="flex items-center gap-2 text-nature-800/60">
                                <Download className="w-5 h-5" />
                                <span className="font-body">MP3 Download</span>
                            </div>
                            <ShareButton title={recording.title} url={`/recording/${recording.slug}`} />
                        </div>
                        
                        {/* Opening Paragraph */}
                        <p className="text-nature-800/80 font-body leading-relaxed mb-8 text-lg" data-testid="opening-paragraph">
                            {recording.opening_paragraph || recording.description}
                        </p>

                        {/* Price & CTA */}
                        <div className="bg-white rounded-2xl p-6 shadow-soft" data-testid="price-cta-section">
                            {isFreeRecording ? (
                                <>
                                    <div className="flex items-center justify-between mb-6">
                                        <span className="text-3xl font-heading font-semibold text-green-600">
                                            FREE
                                        </span>
                                        <span className="text-sm text-nature-800/60 font-body">
                                            Instant download • Yours forever
                                        </span>
                                    </div>
                                    
                                    <Button
                                        onClick={handleFreeDownload}
                                        disabled={downloading}
                                        size="lg"
                                        className="w-full rounded-full bg-copper-500 hover:bg-copper-600"
                                        data-testid="get-free-recording"
                                    >
                                        <Download className="w-5 h-5 mr-2" />
                                        {downloading ? 'Preparing download…' : 'Get Free Recording'}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <div className="flex items-center justify-between mb-6">
                                        <span className="text-3xl font-heading font-semibold text-copper-500">
                                            €{recording.price.toFixed(2)}
                                        </span>
                                        <span className="text-sm text-nature-800/60 font-body">
                                            Instant download • Yours forever
                                        </span>
                                    </div>
                                    
                                    <Button
                                        onClick={handleAddToCart}
                                        size="lg"
                                        className={`w-full rounded-full transition-all ${
                                            inCart 
                                                ? 'bg-nature-800 hover:bg-nature-800' 
                                                : 'bg-copper-500 hover:bg-copper-600'
                                        }`}
                                        data-testid="add-to-cart-detail"
                                    >
                                        {inCart ? (
                                            <>
                                                <Check className="w-5 h-5 mr-2" />
                                                Added to Cart
                                            </>
                                        ) : (
                                            <>
                                                <ShoppingCart className="w-5 h-5 mr-2" />
                                                Add to Cart
                                            </>
                                        )}
                                    </Button>
                                    
                                    {inCart && (
                                        <Link to="/cart" className="block mt-4">
                                            <Button 
                                                variant="outline" 
                                                className="w-full rounded-full border-nature-200"
                                                data-testid="view-cart-btn"
                                            >
                                                View Cart & Checkout
                                            </Button>
                                        </Link>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Features */}
                        <div className="mt-8 grid grid-cols-2 gap-4">
                            {[
                                'High-quality MP3',
                                'Instant download',
                                'Listen offline',
                                'Yours to keep'
                            ].map((feature, i) => (
                                <div key={i} className="flex items-center gap-2 text-nature-800/70 text-sm font-body">
                                    <Check className="w-4 h-4 text-copper-500" />
                                    {feature}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* 8-Part Content Section */}
                <div className="mt-16 max-w-4xl mx-auto space-y-12">
                    
                    {/* What This Session Is For */}
                    {recording.what_for && recording.what_for.length > 0 && (
                        <section className="bg-white rounded-2xl p-8 shadow-soft" data-testid="what-for-section">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-full bg-copper-500/10 flex items-center justify-center">
                                    <Target className="w-5 h-5 text-copper-500" />
                                </div>
                                <h2 className="font-heading text-xl font-semibold text-nature-900">
                                    What This Session Is For
                                </h2>
                            </div>
                            <ul className="space-y-3">
                                {recording.what_for.map((item, i) => (
                                    <li key={i} className="flex items-start gap-3 text-nature-800/80 font-body">
                                        <Check className="w-5 h-5 text-copper-500 mt-0.5 flex-shrink-0" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {/* How It Works */}
                    {recording.how_it_works && (
                        <section className="bg-nature-100/50 rounded-2xl p-8" data-testid="how-it-works-section">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-full bg-copper-500/10 flex items-center justify-center">
                                    <Sparkles className="w-5 h-5 text-copper-500" />
                                </div>
                                <h2 className="font-heading text-xl font-semibold text-nature-900">
                                    How It Works
                                </h2>
                            </div>
                            <div className="text-nature-800/80 font-body leading-relaxed space-y-4">
                                {recording.how_it_works.split('\n\n').map((para, i) => (
                                    <p key={i}>{para}</p>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* What To Expect */}
                    {recording.what_to_expect && (
                        <section className="bg-white rounded-2xl p-8 shadow-soft" data-testid="what-to-expect-section">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-full bg-copper-500/10 flex items-center justify-center">
                                    <Eye className="w-5 h-5 text-copper-500" />
                                </div>
                                <h2 className="font-heading text-xl font-semibold text-nature-900">
                                    What To Expect
                                </h2>
                            </div>
                            <div className="text-nature-800/80 font-body leading-relaxed space-y-4">
                                {recording.what_to_expect.split('\n\n').map((para, i) => (
                                    <p key={i}>{para}</p>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Who This Is For */}
                    {recording.who_for && (
                        <section className="bg-nature-100/50 rounded-2xl p-8" data-testid="who-for-section">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-full bg-copper-500/10 flex items-center justify-center">
                                    <Users className="w-5 h-5 text-copper-500" />
                                </div>
                                <h2 className="font-heading text-xl font-semibold text-nature-900">
                                    Who This Is For
                                </h2>
                            </div>
                            <div className="text-nature-800/80 font-body leading-relaxed space-y-4">
                                {recording.who_for.split('\n\n').map((para, i) => (
                                    <p key={i}>{para}</p>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Listen to the Preview - Note */}
                    {recording.preview_note && (
                        <section className="bg-copper-500/5 border border-copper-500/20 rounded-2xl p-8" data-testid="preview-note-section">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-full bg-copper-500/10 flex items-center justify-center">
                                    <Volume2 className="w-5 h-5 text-copper-500" />
                                </div>
                                <h2 className="font-heading text-xl font-semibold text-nature-900">
                                    Listen to the Preview
                                </h2>
                            </div>
                            <p className="text-nature-800/80 font-body leading-relaxed">
                                {recording.preview_note}
                            </p>
                        </section>
                    )}

                    {/* Closing Line */}
                    {recording.closing && (
                        <section className="text-center py-8" data-testid="closing-section">
                            <p className="text-lg sm:text-xl font-body italic text-nature-800/90 max-w-2xl mx-auto leading-relaxed">
                                &ldquo;{recording.closing}&rdquo;
                            </p>
                        </section>
                    )}

                    {/* Bottom CTA */}
                    <div className="bg-white rounded-2xl p-8 shadow-soft text-center" data-testid="bottom-cta">
                        {isFreeRecording ? (
                            <>
                                <p className="text-nature-800/70 font-body mb-4">
                                    Ready to experience deep, restful sleep?
                                </p>
                                <Button
                                    onClick={handleFreeDownload}
                                    disabled={downloading}
                                    size="lg"
                                    className="rounded-full bg-copper-500 hover:bg-copper-600 px-8"
                                    data-testid="bottom-get-free"
                                >
                                    <Download className="w-5 h-5 mr-2" />
                                    {downloading ? 'Preparing download…' : 'Get Your Free Recording'}
                                </Button>
                            </>
                        ) : (
                            <>
                                <p className="text-nature-800/70 font-body mb-4">
                                    Ready to begin your journey?
                                </p>
                                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                    <Button
                                        onClick={handleAddToCart}
                                        size="lg"
                                        className={`rounded-full px-8 ${
                                            inCart 
                                                ? 'bg-nature-800 hover:bg-nature-800' 
                                                : 'bg-copper-500 hover:bg-copper-600'
                                        }`}
                                        data-testid="bottom-add-to-cart"
                                    >
                                        {inCart ? (
                                            <>
                                                <Check className="w-5 h-5 mr-2" />
                                                Added to Cart
                                            </>
                                        ) : (
                                            <>
                                                <ShoppingCart className="w-5 h-5 mr-2" />
                                                Add to Cart — €{recording.price.toFixed(2)}
                                            </>
                                        )}
                                    </Button>
                                    {inCart && (
                                        <Link to="/cart">
                                            <Button 
                                                variant="outline" 
                                                size="lg"
                                                className="rounded-full border-nature-200 px-8"
                                            >
                                                View Cart & Checkout
                                            </Button>
                                        </Link>
                                    )}
                                </div>
                            </>
                        )}
                        
                        {/* Tip with blog link */}
                        <div className="mt-6 p-4 bg-nature-100 rounded-xl max-w-md mx-auto">
                            <p className="text-nature-800/70 text-sm font-body">
                                <span className="font-medium text-nature-900">Tip:</span> For best results, listen daily for at least 21 days.{' '}
                                <Link to="/blog/the-science-behind-hypnotherapy" className="text-copper-600 hover:text-copper-700 underline">
                                    Why repetition matters for change →
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Related Recordings */}
                {relatedRecordings.length > 0 && (
                    <div className="mt-24">
                        <h2 className="font-heading text-2xl font-light text-nature-900 mb-8">
                            More in <span className="italic">{recording.category}</span>
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {relatedRecordings.map((rel, index) => (
                                <Link 
                                    key={rel.id}
                                    to={`/recording/${rel.slug}`}
                                    className="product-card block bg-white rounded-2xl overflow-hidden shadow-soft opacity-0 animate-fade-in-up"
                                    style={{ animationDelay: `${index * 0.1}s` }}
                                    data-testid={`related-${rel.id}`}
                                >
                                    <div className="aspect-video relative overflow-hidden">
                                        <img 
                                            src={rel.cover_image} 
                                            alt={rel.title}
                                            className="w-full h-full object-cover"
                                            referrerPolicy="no-referrer"
                                        />
                                    </div>
                                    <div className="p-5">
                                        <h3 className="font-heading text-lg font-semibold text-nature-900 mb-2 line-clamp-1">
                                            {rel.title}
                                        </h3>
                                        <div className="flex items-center justify-between">
                                            <span className="text-copper-500 font-heading font-semibold">
                                                €{rel.price.toFixed(2)}
                                            </span>
                                            <span className="text-sm text-nature-800/60 font-body">
                                                {rel.duration}
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RecordingDetail;
